import { getAuthToken } from "./authService";
import api from "../api";

const API_BASE_URL = `${
  process.env.REACT_APP_API_URL || "http://localhost:3001"
}/api/clientscontacts`;

class ClientsContactsService {
  // Get contacts with DataTables support
  async getContacts(params = {}) {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching contacts:", error);
      throw error;
    }
  }

  // Get all contacts (simple list)
  async getAllContacts() {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching all contacts:", error);
      throw error;
    }
  }

  // Update main contact status
  async updateMainContact(contactId, isMainContact) {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/main-contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          contactId,
          isMainContact,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error updating main contact:", error);
      throw error;
    }
  }

  // Delete contact
  async deleteContact(userId) {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/delete`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error deleting contact:", error);
      throw error;
    }
  }

  // Export emails
  async exportEmails() {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/export-emails`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "client_contacts_emails.csv";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return true;
    } catch (error) {
      console.error("Error exporting emails:", error);
      throw error;
    }
  }

  // Mock data function removed - using live database only

  // Export to CSV
  exportCSV(contacts) {
    const headers = [
      "Name",
      "Company",
      "Company Status",
      "Company Address",
      "Web",
      "Company Phone",
      "VIP Amount",
      "Savings Made",
      "Email",
      "Phone",
      "Department",
      "Job Title",
      "Reports To",
      "Address",
      "Description",
      "Company Internal Info",
    ];

    const csvContent = [
      headers.join(","),
      ...contacts.map((contact) =>
        [
          `"${contact.name || ""}"`,
          `"${contact.company || ""}"`,
          `"${contact.clientStatus || ""}"`,
          `"${contact.company_address || ""}"`,
          `"${contact.web || ""}"`,
          `"${contact.companyPhoneNumber || ""}"`,
          `"${contact.vip_amount || 0}"`,
          `"${contact.savings_made || 0}"`,
          `"${contact.userEmail || ""}"`,
          `"${contact.userPhoneNumber || ""}"`,
          `"${contact.department || ""}"`,
          `"${contact.job_title || ""}"`,
          `"${contact.reports_to || ""}"`,
          `"${contact.address || ""}"`,
          `"${contact.description || ""}"`,
          `"${contact.internal_info || ""}"`,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "client_contacts.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Export to PDF (placeholder - would need a PDF library)
  exportPDF(contacts) {
    try {
      // Import jsPDF and jsPDF-AutoTable
      import("jspdf")
        .then(({ default: jsPDF }) => {
          import("jspdf-autotable")
            .then(({ default: autoTable }) => {
              const doc = new jsPDF();

              // Add title
              doc.setFontSize(18);
              doc.text("Client Contacts Report", 14, 22);
              doc.setFontSize(12);
              doc.text(
                `Generated on: ${new Date().toLocaleDateString()}`,
                14,
                32
              );

              // Define table columns
              const columns = [
                "Name",
                "Company",
                "Company Status",
                "Company Address",
                "Web",
                "Company Phone",
                "VIP Amount",
                "Savings Made",
                "Email",
                "Phone",
                "Department",
                "Job Title",
                "Reports To",
                "Address",
                "Description",
                "Company Internal Info",
              ];

              // Prepare data
              const data = contacts.map((contact) => [
                contact.name || "-",
                contact.company ? contact.company.replace(/<[^>]*>/g, "") : "-", // Remove HTML tags
                contact.clientStatus || "-",
                contact.company_address || "-",
                contact.web || "-",
                contact.companyPhoneNumber || "-",
                contact.vip_amount
                  ? `£${contact.vip_amount.toLocaleString()}`
                  : "-",
                contact.savings_made
                  ? `£${contact.savings_made.toLocaleString()}`
                  : "-",
                contact.email ? contact.email.replace(/<[^>]*>/g, "") : "-", // Remove HTML tags
                contact.phone || "-",
                contact.department || "-",
                contact.job_title || "-",
                contact.reports_to || "-",
                contact.address || "-",
                contact.description || "-",
                contact.internal_info || "-",
              ]);

              // Add table to PDF
              autoTable(doc, {
                head: [columns],
                body: data,
                startY: 40,
                styles: {
                  fontSize: 8,
                  cellPadding: 2,
                },
                headStyles: {
                  fillColor: [51, 122, 183],
                  textColor: 255,
                  fontStyle: "bold",
                },
                alternateRowStyles: {
                  fillColor: [245, 245, 245],
                },
                columnStyles: {
                  0: { cellWidth: 25 }, // Name
                  1: { cellWidth: 30 }, // Company
                  2: { cellWidth: 20 }, // Company Status
                  3: { cellWidth: 35 }, // Company Address
                  4: { cellWidth: 25 }, // Web
                  5: { cellWidth: 25 }, // Company Phone
                  6: { cellWidth: 20 }, // VIP Amount
                  7: { cellWidth: 20 }, // Savings Made
                  8: { cellWidth: 35 }, // Email
                  9: { cellWidth: 20 }, // Phone
                  10: { cellWidth: 25 }, // Department
                  11: { cellWidth: 25 }, // Job Title
                  12: { cellWidth: 25 }, // Reports To
                  13: { cellWidth: 35 }, // Address
                  14: { cellWidth: 30 }, // Description
                  15: { cellWidth: 35 }, // Company Internal Info
                },
                didDrawPage: function (data) {
                  // Add page numbers
                  const pageCount = doc.internal.getNumberOfPages();
                  for (let i = 1; i <= pageCount; i++) {
                    doc.setPage(i);
                    doc.setFontSize(10);
                    doc.text(
                      `Page ${i} of ${pageCount}`,
                      doc.internal.pageSize.width - 20,
                      doc.internal.pageSize.height - 10
                    );
                  }
                },
              });

              // Save the PDF
              doc.save("client-contacts-report.pdf");
            })
            .catch((error) => {
              console.error("Error loading jsPDF-AutoTable:", error);
              // Fallback to CSV
              this.exportCSV(contacts);
            });
        })
        .catch((error) => {
          console.error("Error loading jsPDF:", error);
          // Fallback to CSV
          this.exportCSV(contacts);
        });
    } catch (error) {
      console.error("Error generating PDF:", error);
      // Fallback to CSV
      this.exportCSV(contacts);
    }
  }

  // Search contacts
  async searchContacts(searchTerm) {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          search: { value: searchTerm },
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error searching contacts:", error);
      throw error;
    }
  }
}

export default new ClientsContactsService();
